import { produce } from 'immer';
import { Action, ActionType, createAsyncAction, createReducer } from 'typesafe-actions';
import { Observable, of, from, OperatorFunction } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { toReduxError, type ReduxError } from '@shared/errors';
import { ofType } from 'redux-observable';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { TypedEpic } from '../types';

type GetIdpCountryResponse = {
  countryName: string;
  additionalInfo: string;
};

export enum Actions {
  GET_IDP_COUNTRIES = 'naf/idpCountries/GET_IDP_COUNTRIES',
  GET_IDP_COUNTRIES_SUCCESS = 'naf/idpCountries/GET_IDP_COUNTRIES_SUCCESS',
  GET_IDP_COUNTRIES_FAIL = 'naf/idpCountries/GET_IDP_COUNTRIES_FAIL',
  GET_IDP_COUNTRIES_CANCEL = 'naf/idpCountries/GET_IDP_COUNTRIES_CANCEL',
}

export interface State {
  data: { countryName: string; additionalInfo: string }[];
  isUpdating: boolean;
  errorState?: any;
}

export const initialState: State = {
  data: [],
  isUpdating: false,
};

export const actions = {
  getIdpCountries: createAsyncAction(
    Actions.GET_IDP_COUNTRIES, // request payload creator
    Actions.GET_IDP_COUNTRIES_SUCCESS, // success payload creator
    Actions.GET_IDP_COUNTRIES_FAIL, // failure payload creator
    Actions.GET_IDP_COUNTRIES_CANCEL, // optional cancel payload creator
  )<undefined, GetIdpCountryResponse[], ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getIdpCountries.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = true;
    }),
  )
  .handleAction(actions.getIdpCountries.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.data = action.payload;
    }),
  )
  .handleAction(actions.getIdpCountries.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.errorState = action.payload;
    }),
  )
  .handleAction(actions.getIdpCountries.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
    }),
  );

export const getIdpCountriesEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_IDP_COUNTRIES),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getIdpCountries.request>[]
    >,
    switchMap(() =>
      from(
        apiClient.get<GetIdpCountryResponse[]>(ApiType.apimNafNoApi, {
          url: ApiPaths[ApiType.apimNafNoApi].ifkcountries,
        }),
      ).pipe(
        map((response) => actions.getIdpCountries.success(response.data)),
        catchError((err) =>
          of(
            actions.getIdpCountries.failure(
              toReduxError(
                err,
                'Oops, vi har problemer med motoren... Kunne ikke hente landlisten! Ta kontakt med kundesenteret hvis problemet fortsetter!',
              ),
            ),
          ),
        ),
      ),
    ),
  );

export const epics: TypedEpic[] = [getIdpCountriesEpic];
